import { useState, useEffect, useMemo } from "react";
import { platformApi } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { format } from "date-fns";
import { UsersThree, HandCoins, TrendUp, Receipt, Coin, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Modal that drills down into one of the 4 platform KPIs. View is determined
 * by the `topic` prop: payouts | income | members | null.
 *
 * All data shown here is member-safe (no phones, emails, family details).
 */
export default function PlatformKpiDrillDown({ topic, onClose }) {
  const open = topic !== null && topic !== undefined;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="kpi-drilldown-dialog">
        {topic === "payouts" && <PayoutsView />}
        {topic === "income" && <IncomeBreakdownView />}
        {topic === "members" && <ActiveMembersView />}
      </DialogContent>
    </Dialog>
  );
}

function PayoutsView() {
  const [rows, setRows] = useState(null);
  useEffect(() => { platformApi.payouts().then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  const total = (rows || []).reduce((s, r) => s + (r.payoutAmount || 0), 0);

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><HandCoins className="w-5 h-5 text-orange-700" weight="duotone" /> Bereavement Payouts</DialogTitle>
        <DialogDescription>
          Every family the welfare fund has supported, with the exact amount paid out. Built so members can see directly how their contributions help the community.
        </DialogDescription>
      </DialogHeader>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="bg-secondary/40 rounded-xl px-4 py-3 text-center">
          <p className="text-2xl font-bold font-body text-foreground">{(rows || []).length}</p>
          <p className="text-xs text-muted-foreground">Families supported</p>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-center">
          <p className="text-2xl font-bold font-body text-orange-700">KES {total.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Total paid out</p>
        </div>
      </div>

      {rows === null ? (
        <div className="space-y-2 mt-4">{[1, 2, 3].map((i) => <div key={i} className="h-14 bg-muted rounded-xl animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-center text-muted-foreground py-8">No payouts yet.</p>
      ) : (
        <div className="mt-4 divide-y divide-border rounded-xl border border-border overflow-hidden" data-testid="payouts-list">
          {rows.map((r) => (
            <div key={r.id} className="px-4 py-3 flex items-start gap-3" data-testid={`payout-row-${r.id}`}>
              {r.imageUrl ? (
                <img src={r.imageUrl} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <UsersThree className="w-5 h-5 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-foreground truncate">{r.deceasedName}</p>
                <p className="text-xs text-muted-foreground">
                  Passed {r.dateOfDeath ? format(new Date(r.dateOfDeath), "dd MMM yyyy") : "—"}
                  {r.totalContributed > 0 && <> · contributions: KES {r.totalContributed.toLocaleString()}</>}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-bold font-body text-orange-700">KES {r.payoutAmount.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">{r.payoutDate ? format(new Date(r.payoutDate), "dd MMM yyyy") : ""}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function IncomeBreakdownView() {
  const [data, setData] = useState(null);
  useEffect(() => { platformApi.incomeBreakdown().then(({ data }) => setData(data)).catch(() => setData(null)); }, []);

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><TrendUp className="w-5 h-5 text-emerald-700" weight="duotone" /> Income Breakdown</DialogTitle>
        <DialogDescription>How the welfare fund's income is composed and where it has gone so far.</DialogDescription>
      </DialogHeader>

      {!data ? (
        <div className="space-y-2 mt-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-14 bg-muted rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="income-breakdown-list">
          <Row icon={Coin} colour="emerald" label="Bereavement contributions" value={data.contributionsTotal} sub={`${data.contributionsCount.toLocaleString()} payments from ${data.uniqueContributors.toLocaleString()} unique members`} />
          <Row icon={Receipt} colour="emerald" label="Registration fees" value={data.registrationFeesTotal} sub={`${data.registrationFeesCount.toLocaleString()} members paid`} />
          <div className="border-t border-border pt-3">
            <Row icon={TrendUp} colour="emerald" label="Total income (all-time)" value={data.totalIncome} bold />
          </div>
          <div className="space-y-3 mt-2 pt-3 border-t border-border">
            <Row icon={HandCoins} colour="orange" label="Benefit payouts" value={-data.totalPayouts} sub="Money going to bereaved families" />
            <Row icon={Receipt} colour="red" label="Operating expenses" value={-data.totalExpenses} sub="Stationery, transport, refreshments, etc." />
          </div>
          <div className="border-t border-border pt-3">
            <Row icon={TrendUp} colour={data.netSurplus >= 0 ? "primary" : "red"} label="Net surplus" value={data.netSurplus} bold />
            <Row icon={Coin} colour="default" label="Bank balance (per ledger)" value={data.bankBalance} sub="What the treasurer reports holding" />
          </div>
        </div>
      )}
    </>
  );
}

function Row({ icon: Icon, colour, label, value, sub, bold }) {
  const palette = {
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", txt: "text-emerald-700" },
    orange: { bg: "bg-orange-50", border: "border-orange-200", txt: "text-orange-700" },
    red: { bg: "bg-red-50", border: "border-red-200", txt: "text-red-700" },
    primary: { bg: "bg-primary/10", border: "border-primary/30", txt: "text-primary" },
    default: { bg: "bg-secondary/40", border: "border-border", txt: "text-foreground" },
  }[colour] || { bg: "bg-card", border: "border-border", txt: "text-foreground" };
  const sign = value < 0 ? "−" : "";
  return (
    <div className={`${palette.bg} ${palette.border} border rounded-xl px-4 py-3 flex items-center gap-3`}>
      <Icon className={`w-5 h-5 ${palette.txt} shrink-0`} weight="duotone" />
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${bold ? "font-bold" : "font-medium"} text-foreground truncate`}>{label}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
      <p className={`${bold ? "text-lg font-bold" : "text-base font-semibold"} ${palette.txt} font-body shrink-0`}>
        {sign}KES {Math.abs(value).toLocaleString()}
      </p>
    </div>
  );
}

function ActiveMembersView() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { platformApi.activeMembers().then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  const filtered = useMemo(() => {
    if (!rows) return null;
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((m) => m.name.toLowerCase().includes(needle) || m.ukc.toLowerCase().includes(needle));
  }, [rows, q]);

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><UsersThree className="w-5 h-5 text-primary" weight="duotone" /> Active Members</DialogTitle>
        <DialogDescription>Everyone currently on the welfare register. Personal details (phone, family, area) are hidden — see the full Directory tab if you need them.</DialogDescription>
      </DialogHeader>

      <div className="mt-3 relative">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or UKC…"
          className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="active-members-search"
        />
      </div>

      {!rows ? (
        <div className="space-y-2 mt-4">{[1, 2, 3].map((i) => <div key={i} className="h-12 bg-muted rounded-xl animate-pulse" />)}</div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mt-2">
            Showing {filtered.length.toLocaleString()} of {rows.length.toLocaleString()} members
          </p>
          <div className="mt-2 divide-y divide-border rounded-xl border border-border overflow-hidden max-h-[55vh] overflow-y-auto" data-testid="active-members-list">
            {filtered.map((m) => (
              <div key={m.id} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`member-row-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-foreground truncate">{m.name}</p>
                  {m.area && <p className="text-xs text-muted-foreground">{m.area}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-mono font-semibold text-primary">{m.ukc}</span>
                  {m.status === "probation" && <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-md">Probation</span>}
                  {m.status === "approved" && <span className="text-[10px] uppercase tracking-wide bg-blue-100 text-blue-900 px-1.5 py-0.5 rounded-md">New</span>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
